import React, { Fragment, useState, useEffect, useMemo } from "react";
import {
  Grid,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  tableCellClasses,
  Paper,
  CircularProgress,
  Checkbox,
  Divider,
  InputAdornment,
} from "@mui/material";
import { useAuth } from "context/UseContext";
import { useNavigate } from "react-router-dom";
import { FontFamily, SearchIcon } from "assets";
import Colors from "assets/Style/Colors";
import { PrimaryButton } from "components/Buttons";
import { useForm } from "react-hook-form";
import InputField from "components/Input";
import { ErrorToaster } from "components/Toaster";
import PaymentStatus from "components/PaymentStatus";
import SupplierServices from "services/Supplier";
import styled from "@emotion/styled";
import DatePicker from "components/DatePicker";
import SelectField from "components/Select";
import FinanceServices from "services/Finance";

import BankServices from "services/Bank";
import {allowedRolesForCash } from "utils";
const Row = styled(TableRow)(({ theme }) => ({
  border: 0,
}));

const Cell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    border: 0,
    textAlign: "center",
    whiteSpace: "nowrap",
    background: Colors.primary,
    color: Colors.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    textAlign: "center",
    padding: "5px !important",
  },
}));
const formatUSD = (value) => {
  const num = parseFloat(value || 0);
  return `$${isNaN(num) ? "0.00" : num.toFixed(2)}`;
};

const formatAED = (value) => {
  const num = parseFloat(value || 0);
  return `د.إ ${isNaN(num) ? "0.00" : num.toFixed(2)}`;
};

const restrictTwoDecimals = (value) => {
  if (value === "" || value === undefined || value === null) return "";
  const str = String(value);
  const dotIndex = str.indexOf(".");
  if (dotIndex !== -1 && str.length - dotIndex - 1 > 2) {
    return str.substring(0, dotIndex + 3);
  }
  return str;
};
const PayBill = () => {
  const [selectedRows, setSelectedRows] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loader, setLoader] = useState(false);
  const [billDate, setBillDate] = useState(new Date());

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [bills, setBills] = useState([]);

  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [cashierAccounts, setCashierAccounts] = useState([]);
  const [selectedCashierAccount, setSelectedCashierAccount] = useState(null);
  const [firstSelectedSupplierId, setFirstSelectedSupplierId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedSupplierFilter, setSelectedSupplierFilter] = useState("");

  const [submitLoader, setSubmitLoader] = useState(false);
  const {
    register,
    handleSubmit,
     setValue,
    formState: { errors },
  } = useForm();

  const calculateGrandTotalAmount = (billAccounts) => {
    if (!billAccounts || !Array.isArray(billAccounts)) return 0;
    return billAccounts.reduce(
      (total, account) => total + parseFloat(account?.total_amount || 0),
      0,
    );
  };

  const getRemainingDueForBill = (bill) => {
    const totalDue = calculateGrandTotalAmount(bill?.bill_accounts);
    const alreadyPaid = Number(bill?.paid_amount || 0);
    const remaining =
      totalDue - (Number.isFinite(alreadyPaid) ? alreadyPaid : 0);
    return remaining > 0 ? remaining : 0;
  };

  const handleBillDate = (newDate) => {
    try {
      if (newDate == "Invalid Date") {
        setBillDate("invalid");
        return;
      }
      setBillDate(new Date(newDate));
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const handleCheckboxChange = (event, row) => {
    const checked = event.target.checked;

    if (checked) {
      if (selectedRows.length === 0) {
        setFirstSelectedSupplierId(row?.suppliers?.id);
      }

      if (
        firstSelectedSupplierId &&
        row?.suppliers?.id !== firstSelectedSupplierId
      ) {
        ErrorToaster("You can only select rows with the same supplier.");
        return;
      }

      setSelectedRows((prevSelectedRows) => [
        ...prevSelectedRows,
        {
          id: row.id,
          supplier_id: row?.suppliers?.id,
          addedAmount: getRemainingDueForBill(row),
          paid: Number(row?.paid_amount || 0),
          balance: getRemainingDueForBill(row),
        },
      ]);
    } else {
      setSelectedRows((prevSelectedRows) =>
        prevSelectedRows.filter((rowItem) => rowItem.id !== row.id),
      );
    }
  };
  const handleSupplierSelectAll = (supplierId) => {
    if (firstSelectedSupplierId && firstSelectedSupplierId !== supplierId) {
      ErrorToaster("You can only select rows with the same supplier.");
      return;
    }

    const supplierBills = bills.filter(
      (b) => b?.suppliers?.id === supplierId
    );

    const allSelected = supplierBills.every((b) =>
      selectedRows.some((r) => r.id === b.id)
    );

    if (allSelected) {
      setSelectedRows((prev) => prev.filter((r) => r.supplier_id !== supplierId));
      setFirstSelectedSupplierId(null);
    } else {
      if (!firstSelectedSupplierId) setFirstSelectedSupplierId(supplierId);
      const newRows = supplierBills
        .filter((b) => !selectedRows.some((r) => r.id === b.id))
        .map((b) => ({
          id: b.id,
          supplier_id: b?.suppliers?.id,
          addedAmount: getRemainingDueForBill(b),
          paid: Number(b?.paid_amount || 0),
          balance: getRemainingDueForBill(b),
        }));
      setSelectedRows((prev) => [...prev, ...newRows]);
    }
  };
  const getPaymentAccounts = async () => {
    try {
      let params = {
        page: 1,
        limit: 1000,
      };
      const { data } = await FinanceServices.getPaymentAccounts(params);
      const vehicleAcc = data?.cashierAccounts?.rows?.filter(
        (e) => e.unit === "Vehicle" || e.unit === "Shipping",
      );
      if (allowedRolesForCash.includes(user?.role_id)) {
        const userId = user?.ref_id.split("-")[1];
        const filterCashier = vehicleAcc.filter((e) => e.user_id == userId);
        setCashierAccounts(filterCashier);
      } else {
        setCashierAccounts(vehicleAcc);
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const getBanks = async () => {
    try {
      let params = {
        page: 1,
        limit: 250,
      };
      const { data } = await BankServices.getBanks(params);
      const activeBanks = data?.banks?.rows.filter((bank) => bank.is_active);
      const bankArray = [];
      activeBanks
        .filter((e) => e.currency && e?.guc_account)
        .forEach((e) => {
          let obj = {
            ...e,
            name: e?.guc_account?.name,
          };
          bankArray.push(obj);
        });
      setBankAccounts(bankArray);
    } catch (error) {
      ErrorToaster(error);
    }
  };
const supplierOptions = useMemo(() => {
  const map = new Map();
  bills.forEach((bill) => {
    const sup = bill?.suppliers;
    if (sup?.id && !map.has(sup.id)) {
      map.set(sup.id, { id: sup.id, name: sup.company_name });
    }
  });
  return Array.from(map.values());
}, [bills]);
  const AED_PER_USD = 3.675;

  const refNo = useMemo(() => {
    return (selectedRows || []).reduce(
      (sum, row) => sum + Number(row?.id || 0),
      0,
    );
  }, [selectedRows]);

  const totals = useMemo(() => {
    let totalAED = 0;
    let totalUSD = 0;

    for (const r of selectedRows || []) {
      const addedAmount = parseFloat(r?.addedAmount || 0);

      if (isNaN(addedAmount)) continue;

      totalUSD += addedAmount;
      totalAED += addedAmount * AED_PER_USD;
    }

    return {
      aed: totalAED > 0 ? totalAED.toFixed(2) : "0.00",
      usd: totalUSD > 0 ? totalUSD.toFixed(2) : "0.00",
    };
  }, [selectedRows]);

  const calculateTotalAmount = (billAccounts) => {
    return billAccounts.reduce(
      (total, account) => total + parseFloat(account?.bill_amount),
      0,
    );
  };

  const calculateVatAmount = (billAccounts) => {
    return billAccounts.reduce(
      (total, account) => total + parseFloat(account?.vat_amount),
      0,
    );
  };
  const handleAddAmountChange = (newValue, rowId) => {
    const restricted = restrictTwoDecimals(newValue);
    const rowItem = bills.find((item) => item.id === rowId);
    const remainingDue = getRemainingDueForBill(rowItem);
    const nextValue = Number(restricted || 0);

    if (nextValue <= remainingDue) {
      setSelectedRows((prevSelectedRows) =>
        prevSelectedRows.map((row) =>
          row.id === rowId
            ? {
              ...row,
              addedAmount: restricted,
              balance: parseFloat((remainingDue - nextValue).toFixed(2)),
            }
            : row,
        ),
      );
    } else {
      ErrorToaster("The amount cannot be greater than the total due.");
    }
  };

  // const handleAddAmountChange = (newValue, rowId) => {
  //   const rowItem = bills.find((item) => item.id === rowId);
  //   const remainingDue = getRemainingDueForBill(rowItem);
  //   const nextValue = Number(newValue || 0);

  //   if (nextValue <= remainingDue) {
  //     setSelectedRows((prevSelectedRows) =>
  //       prevSelectedRows.map((row) =>
  //         row.id === rowId
  //           ? {
  //               ...row,
  //               addedAmount: nextValue,
  //               balance: remainingDue - nextValue,
  //             }
  //           : row,
  //       ),
  //     );
  //   } else {
  //     ErrorToaster("The amount cannot be greater than the total due.");
  //   }
  // };

const [filters, setFilters] = useState({});

const getBill = async (filter = {}) => {
  setLoader(true);
  try {
    const updatedFilters = { ...filters, ...filter };
    setFilters(updatedFilters);
    
    const params = {
      approved: true,
      status: false,
      ...updatedFilters,
    };
    console.log("Sending params:", params); 
    const data = await SupplierServices.getBill(params);
    setBills(data?.data?.bills?.rows);
  } catch (error) {
    ErrorToaster(error);
  } finally {
    setLoader(false);
  }
};

const handleFilter = (data) => {
  getBill(data);
};


  const onSubmit = async (formData) => {
    setSubmitLoader(true);
    try {
      const payments = selectedRows.map((row) => ({
        bill_id: row.id,
        paid_amount: row.addedAmount,
      }));
      const payload = {
        ref_no: refNo,
        supplier_id: firstSelectedSupplierId,
        notes: formData.notes,
        payments: payments,
        pay_bill_date: billDate,
        account_id:
          selectedBankAccount?.guc_account_id || selectedCashierAccount?.id,
        payment_method: selectedPaymentMethod?.name,
        total_amount_aed: totals.aed,
        total_amount_usd: totals.usd,
      };

      const response = await SupplierServices.payBill(payload);
      console.log(response);
      if (response?.status) {
        setSelectedRows([]);
        setFirstSelectedSupplierId(null);
        navigate("/paid-bills-list", { replace: true });
      }
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setSubmitLoader(false);
    }
  };

  useEffect(() => {
    getBill();
    getBanks();
    getPaymentAccounts();
  }, []);

  useEffect(() => {
    if (!selectedPaymentMethod?.id) return;

    if (selectedPaymentMethod?.id === "cash") {
      setSelectedBankAccount(null);

      if (!selectedCashierAccount && (cashierAccounts || []).length > 0) {
        const defaultCashAccount =
          cashierAccounts.find((a) => a?.currency === "USD") ||
          cashierAccounts[0];
        setSelectedCashierAccount(defaultCashAccount);
        setValue("cash", defaultCashAccount);
      }
      return;
    }

    if (selectedPaymentMethod?.id === "bank") {
      setSelectedCashierAccount(null);
      return;
    }
  }, [selectedPaymentMethod?.id, cashierAccounts, selectedCashierAccount]);

  return (
    <Box sx={{ m: 4, mb: 8 }}>
      <Typography
        variant="h2"
        sx={{
          color: "#094526",
          fontFamily: "Helvetica",
          fontSize: "32px",
          fontStyle: "Bold",
          fontWeight: 700,
          mb: 4,
          mt: 5,
          mr: 4,
        }}
      >
        Pay Bills
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <InputField
            size={"small"}
            inputStyle={{ backgroundColor: "#f5f5f5" }}
            label={"Search "}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            placeholder={"Search"}
           register={register("billSearch", {
  onChange: (e) => handleFilter({ search: e.target.value }),
            })}
          />
        </Grid>

      <Grid item xs={12} sm={3}>
  <SelectField
    label={"Filter by Supplier"}
    options={supplierOptions}  
    selected={supplierOptions.find(s => s.id === selectedSupplierFilter) || null}
    onSelect={(value) => {
      setSelectedSupplierFilter(value?.id || "");
      handleFilter({ supplier_id: value?.id || null });
    }}
    register={register("supplierFilter")}
  />
</Grid>
      </Grid>
      <TableContainer
        component={Paper}
        sx={{ maxHeight: "calc(100vh - 300px)", mt: 5 }}
      >
        <Table stickyHeader sx={{ minWidth: 500 }}>
          <TableHead>
            <TableRow>
              {/* <Cell>
                <Checkbox
                  sx={{ color: "white", "&.Mui-checked": { color: "white" }, "&.Mui-disabled": { color: "grey" } }}
                  checked={false}
                  onChange={() => firstSelectedSupplierId && handleSupplierSelectAll(firstSelectedSupplierId)}
                  disabled={!firstSelectedSupplierId}
                /> Select
              </Cell> */}
              <Cell>
                <Checkbox
                  sx={{ color: "white", "&.Mui-checked": { color: "white" }, "&.Mui-disabled": { color: "grey" } }}
                  checked={firstSelectedSupplierId && bills
                    .filter((b) => b?.suppliers?.id === firstSelectedSupplierId)
                    .every((b) => selectedRows.some((r) => r.id === b.id))}
                  onChange={() => firstSelectedSupplierId && handleSupplierSelectAll(firstSelectedSupplierId)}
                  disabled={!firstSelectedSupplierId}
                /> Select
              </Cell>
              <Cell>Bill no.</Cell>
              <Cell>Supp. Name</Cell>
              <Cell>Net. Amt ($)</Cell>
              <Cell>VAT ($)</Cell>
              <Cell>Total Due ($)</Cell>
              <Cell>Paid ($)</Cell>
              <Cell>Add Net Amount ($)</Cell>
              <Cell>Balance ($)</Cell>
              <Cell>Past Due Days</Cell>
              <Cell>Pmt Status</Cell>
              <Cell>Approval Status</Cell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loader ? (
             bills.length > 0 ? (
                <Fragment>
                 {bills.map((item, index) => {
                    const isSelected = selectedRows.some(
                      (row) => row.id === item.id,
                    );

                    const selectedRow = selectedRows.find(
                      (row) => row.id === item.id,
                    );
                    const addAmount = selectedRow ? selectedRow.addedAmount : 0;
                    const totalDue = calculateGrandTotalAmount(
                      item?.bill_accounts,
                    );
                    const alreadyPaid = Number(item?.paid_amount || 0);
                    const remainingDue =
                      totalDue -
                      (Number.isFinite(alreadyPaid) ? alreadyPaid : 0);
                    const balance = selectedRow
                      ? selectedRow.balance
                      : remainingDue > 0
                        ? remainingDue
                        : 0;

                    return (
                      <Row
                        key={index}
                        sx={{ bgcolor: index % 2 !== 0 && "#EEFBEE" }}
                      >


                        <Cell>
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => handleCheckboxChange(e, item)}
                          />
                        </Cell>
                        <Cell>{item?.bill_no ?? "-"}</Cell>
                        <Cell>{item?.suppliers?.company_name ?? "-"}</Cell>
                        <Cell>{calculateTotalAmount(item?.bill_accounts)}</Cell>
                        <Cell>{calculateVatAmount(item?.bill_accounts)}</Cell>
                        <Cell>
                          {calculateGrandTotalAmount(item?.bill_accounts)}
                        </Cell>
                        <Cell>
                          {Number.isFinite(Number(item?.paid_amount))
                            ? Number(item?.paid_amount)
                            : 0}
                        </Cell>
                        <Cell>
                          <InputField
                            type="number"
                            value={addAmount}
                            onInput={(e) => {
                              handleAddAmountChange(e.target.value, item?.id);
                            }}
                            size="small"
                            placeholder="Enter amount"
                            disabled={!isSelected}
                          />
                        </Cell>
                        <Cell>{balance}</Cell>
                        <Cell>{item?.suppliers?.credit_days ?? "-"}</Cell>
                        <Cell>
                          <PaymentStatus status={item?.pmt_status} />
                        </Cell>
                        <Cell>
                          {item?.approved === false ? "Pending" : "Approved"}
                        </Cell>
                      </Row>
                    );
                  })}
                </Fragment>
              ) : (
                <Row>
                  <Cell colSpan={12} align="center">
                    No Data Found
                  </Cell>
                </Row>
              )
            ) : (
              <Row>
                <Cell colSpan={12} align="center">
                  <CircularProgress />
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedRows.length > 0 && (
        <>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: "22px", mt: 3 }}
          >
            Review & Pay
          </Typography>
          <Box
            sx={{
              mt: 4,
              border: "1px solid #B3B3B3",
              padding: 3,
              borderRadius: "12px",
              backgroundColor: "#f9f9f9",
            }}
          >
            <Grid container spacing={2}>
              {/* Total Summary */}
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Total Summary
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={5}>
                      Reference No:
                    </Grid>
                    <Grid item xs={7} sx={{ textAlign: "right" }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "#2e8b57",
                          wordBreak: "break-all",
                        }}
                      >
                        {selectedRows.length > 0 ? `REF-${refNo}` : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                    Total د.إ:
                    </Grid>
                    <Grid item xs={7} sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontWeight: 700, color: "#2e8b57" }}>
                        {totals?.aed}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      Total USD:
                    </Grid>
                    <Grid item xs={7} sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontWeight: 700, color: "#2e8b57" }}>
                        {totals?.usd}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid
                item
                xs={1}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: "#B3B3B3" }}
                />
              </Grid>

              <Grid item xs={12} sm={7}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Payment Details
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={4}>
                      <DatePicker
                        label="Date"
                        size="small"
                        value={billDate}
                        onChange={handleBillDate}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <SelectField
                        label="Payment Method"
                        options={[
                          { id: "cash", name: "Cash" },
                          { id: "bank", name: "Bank" },
                        ]}
                        selected={selectedPaymentMethod}
                        onSelect={(value) => setSelectedPaymentMethod(value)}
                      />
                    </Grid>
                    {selectedPaymentMethod?.name === "Cash" && (
                      <Fragment>
                        <Grid item xs={4} sm={4}>
                          <SelectField
                            size={"small"}
                            label={"Cashier Account"}
                            disabled
                            options={
                              selectedCashierAccount
                                ? [selectedCashierAccount]
                                : cashierAccounts
                            }
                            selected={selectedCashierAccount}
                            onSelect={(value) => {
                              setSelectedCashierAccount(value);
                              setValue("cash", value);
                            }}
                            error={errors?.cash?.message}
                            register={register("cash", {
                              required:
                                selectedPaymentMethod?.id === "cash"
                                  ? "Please select cash account."
                                  : false,
                            })}
                          />
                        </Grid>
                      </Fragment>
                    )}
                    {selectedPaymentMethod?.name === "Bank" && (
                      <>
                        <Grid item xs={4} sm={4}>
                          <SelectField
                            size={"small"}
                            label={"Bank Account"}
                            options={bankAccounts}
                            selected={selectedBankAccount}
                            onSelect={(value) => {
                              setSelectedBankAccount(value);
                            }}
                            error={errors?.bank?.message}
                            register={register("bank", {})}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={4}>
                      <InputField
                        disabled={false}
                        size={"small"}
                        label={"Notes"}
                        placeholder={"Enter Notes"}
                        register={register("notes", {
                          // required: "Notes required.",
                        })}
                        error={errors?.notes?.message}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
            <PrimaryButton
              title={"Pay Invoice"}
              onClick={handleSubmit(onSubmit)}
              loading={submitLoader}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default PayBill;
